import { useCallback } from 'react';

interface MappingTableEditorProps {
  mappings: Record<string, string>;
  onChange: (mappings: Record<string, string>) => void;
  sourceLabel?: string;
  targetLabel?: string;
  targetOptions?: string[];
  /** Function that returns dropdown options per-row based on the source key. Takes precedence over targetOptions when it returns a non-empty array. */
  targetOptionsForKey?: (key: string) => string[];
  disabled?: boolean;
  sourceReadOnly?: boolean;
}

export function MappingTableEditor({
  mappings,
  onChange,
  sourceLabel = 'Source',
  targetLabel = 'Target',
  targetOptions,
  targetOptionsForKey,
  disabled = false,
  sourceReadOnly = false,
}: MappingTableEditorProps) {
  const entries = Object.entries(mappings);

  const updateEntry = useCallback(
    (oldKey: string, newKey: string, newValue: string) => {
      const updated: Record<string, string> = {};
      for (const [k, v] of Object.entries(mappings)) {
        if (k === oldKey) {
          updated[newKey] = newValue;
        } else {
          updated[k] = v;
        }
      }
      onChange(updated);
    },
    [mappings, onChange],
  );

  const removeEntry = useCallback(
    (key: string) => {
      const updated = { ...mappings };
      delete updated[key];
      onChange(updated);
    },
    [mappings, onChange],
  );

  const addEntry = useCallback(() => {
    // Generate a unique placeholder key
    let newKey = '';
    let counter = 1;
    while (newKey === '' || newKey in mappings) {
      newKey = `key${counter}`;
      counter++;
    }
    onChange({ ...mappings, [newKey]: '' });
  }, [mappings, onChange]);

  return (
    <div className="space-y-2">
      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {sourceLabel}
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {targetLabel}
              </th>
              {!disabled && (
                <th className="px-3 py-2 w-12">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {entries.length === 0 ? (
              <tr>
                <td
                  colSpan={disabled ? 2 : 3}
                  className="px-3 py-6 text-center text-sm text-gray-400 dark:text-gray-500 italic"
                >
                  No mappings defined. Click &quot;Add Row&quot; to start.
                </td>
              </tr>
            ) : (
              entries.map(([key, val], index) => (
                <tr key={index} className="bg-white dark:bg-gray-900">
                  {/* Source column */}
                  <td className="px-3 py-2">
                    {sourceReadOnly || disabled ? (
                      <span className="inline-block px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 font-mono text-xs">
                        {key}
                      </span>
                    ) : (
                      <input
                        type="text"
                        className="input"
                        value={key}
                        onChange={(e) => updateEntry(key, e.target.value, val)}
                        placeholder="Source key"
                      />
                    )}
                  </td>

                  {/* Target column */}
                  <td className="px-3 py-2">
                    {(() => {
                      const perKeyOptions = targetOptionsForKey?.(key);
                      const effectiveOptions = perKeyOptions && perKeyOptions.length > 0 ? perKeyOptions : targetOptions;
                      if (disabled) {
                        return <span className="text-gray-800 dark:text-gray-200 text-sm">{val}</span>;
                      }
                      if (effectiveOptions) {
                        return (
                          <select
                            className="input"
                            value={val}
                            onChange={(e) => updateEntry(key, key, e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {effectiveOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        );
                      }
                      return (
                        <input
                          type="text"
                          className="input"
                          value={val}
                          onChange={(e) => updateEntry(key, key, e.target.value)}
                          placeholder="Target value"
                        />
                      );
                    })()}
                  </td>

                  {/* Delete button */}
                  {!disabled && (
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeEntry(key)}
                        className="inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-red-600 dark:hover:text-red-400"
                        title={`Remove mapping for "${key}"`}
                        aria-label={`Remove mapping for ${key}`}
                      >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add row button */}
      {!disabled && (
        <button
          type="button"
          onClick={addEntry}
          className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-700 dark:hover:text-primary-400 transition-colors"
        >
          + Add Row
        </button>
      )}
    </div>
  );
}
